import { useState } from 'react';
import { authPostRequest } from 'Api/api';
import SuccessError from 'components/SuccessError';
import { useSelector } from 'react-redux';
import UserForm from 'components/UserForm';
import { emailValidation } from 'utils/email_strength';
import ModalComp from 'components/Modal';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import CenteredWrapper from 'pages/updatePassword/CenteredWrapper';
import { checkMinLength, checkName, checkTextLength, isStringEmpty } from 'utils/utils';

const CreateUser = ({
  addOpenModal,
  setAddOpenModal,
  setSuccess,
  setAlertBar,
  setIsFormSaved,
  isFormSaved
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    stores: []
  });
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [errors, setErrors] = useState({});

  const { userData } = useSelector((store) => store.auth);

  const handleUserInfo = (e) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
    handleError(e.target.name, null);
  };

  const handleError = (input, errorMessage) => {
    setErrors((prevState) => ({ ...prevState, [input]: errorMessage }));
  };

  const updateHandler = () => {
    // Single user validations
    if (isStringEmpty(userInfo.firstName)) {
      handleError('firstName', 'Please enter first name');
    }

    const firstNameMinLength = checkMinLength(userInfo.firstName, 2);
    if (firstNameMinLength) {
      handleError('firstName', 'Please enter at least 2 characters.');
    }
    const firstNameValid = checkTextLength(userInfo.firstName, 50);
    if (firstNameValid) {
      handleError('firstName', 'Maximum 50 characters allowed');
    }

    const firstNameCheck = checkName(userInfo.firstName);
    if (firstNameCheck) {
      handleError(
        'firstName',
        'Please use alphabetic and international characters only for the name '
      );
    }

    if (isStringEmpty(userInfo.lastName)) {
      handleError('lastName', 'Please enter last name');
    }

    const lastNameMinLength = checkMinLength(userInfo.lastName, 2);
    if (lastNameMinLength) {
      handleError('lastName', 'Please enter at least 2 characters.');
    }
    const lastNameValid = checkTextLength(userInfo.lastName, 50);
    if (lastNameValid) {
      handleError('lastName', 'Maximum 50 characters allowed');
    }

    const lastNameCheck = checkName(userInfo.lastName);
    if (lastNameCheck) {
      handleError(
        'lastName',
        'Please use alphabetic and international characters only for the name'
      );
    }

    if (userInfo.stores == undefined) {
      handleError('stores', 'Please select store');
    }

    const emailvalid = emailValidation(userInfo.email);
    handleError('email', emailvalid);

    const emailMaxLength = checkTextLength(userInfo.email, 50);
    if (emailMaxLength) {
      handleError('email', 'Maximum 50 characters allowed ');
    }

    if (
      userInfo.stores &&
      emailvalid == '' &&
      !isStringEmpty(userInfo.firstName) &&
      !isStringEmpty(userInfo.lastName) &&
      !firstNameCheck &&
      !lastNameCheck &&
      !firstNameValid &&
      !lastNameValid &&
      !firstNameMinLength &&
      !lastNameMinLength &&
      !emailMaxLength
    ) {
      setErrors({});
      return true;
    }

    return false;
  };

  //save and add more
  const saveAndAddMore = async (e) => {
    e.preventDefault();
    setSuccess([]);
    const createUser = await handleSubmit();
    if (createUser?.resStatus === 1) {
      setSuccessMessage(createUser.success);

      if (createUser?.success?.length > 0) {
        setUserInfo({ firstName: '', lastName: '', email: '', role: '', stores: userInfo.stores });
      }
      setErrorMessage(createUser.error);
      setIsFormSaved(!isFormSaved);
    }
  };

  //Save and close
  const saveAndClose = async (e) => {
    e.preventDefault();
    setErrorMessage([]);
    setSuccess([]);
    setAlertBar(false);
    const createUser = await handleSubmit();
    if (createUser?.resStatus === 1) {
      if (createUser?.error?.length > 0) {
        setSuccessMessage(createUser.success);
        setErrorMessage(createUser?.error);
      } else {
        setSuccess(createUser?.success);
      }

      if (createUser?.error?.length === 0) {
        setAddOpenModal(false);
      }
      if (createUser?.success?.length > 0) {
        setUserInfo({ firstName: '', lastName: '', email: '', role: '', stores: [] });
      }
      setIsFormSaved(!isFormSaved);
    }

    setAlertBar(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setErrorMessage([]);
    setSuccessMessage([]);
    const isFormValid = updateHandler();
    try {
      const requestParams = {
        userId: userData.userId,
        firstName: userInfo.firstName.trim(),
        lastName: userInfo.lastName.trim(),
        email: userInfo.email.trim(),
        role: 'ROLE_ADMIN',
        stores: userInfo.stores,
        isBulkUpload: 0
      };

      setShowLoader(true);
      setShowAlertBar(false);
      const createUsers = isFormValid && (await authPostRequest('createUser', requestParams));

      setShowLoader(false);
      setShowAlertBar(true);
      return createUsers;
    } catch (err) {
      setErrorMessage([...errorMessage, 'Error']);
      window.scrollTo(0, 0);
    }
  };

  return (
    <CenteredWrapper>
      <ModalComp open={addOpenModal} setOpen={setAddOpenModal} header="Create User">
        {showAlertBar && (
          <SuccessError
            successMessage={successMessage}
            errorMessage={errorMessage}
            setShowAlertBar={setShowAlertBar}
          />
        )}

        {showLoader && <Loader open={showLoader} />}

        <UserForm
          handleSubmit={saveAndAddMore}
          buttonText="Save & Add More"
          buttonClose="Save & Close"
          handleError={handleError}
          handleCancelAction={saveAndClose}
          handleUserInfo={handleUserInfo}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          error={errors}
          setShowLoader={setShowLoader}
        />
      </ModalComp>
    </CenteredWrapper>
  );
};

CreateUser.propTypes = {
  addOpenModal: PropTypes.bool,
  isFormSaved: PropTypes.bool,
  setIsFormSaved: PropTypes.func,
  setAddOpenModal: PropTypes.func,
  setSuccess: PropTypes.func,
  setAlertBar: PropTypes.func
};

export default CreateUser;
